import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Button,
    Flex,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Table,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import config from '../config';
import SelectedGameContext from '../context/SelectedGameContext';
import InventoryDataCaller from '../util/inventoryDataCaller';
import { ProductCost, ProductDetail } from '../interfaces/IInventory';

// TODO: Add optionality
interface ItemDef {
    item_id: string;
    item_name: string;
    item_desc?: string;
    item_type: string;
    item_prop: Record<string, any>;
    item_owner: string;
    creator_license: string[];
    creator_user: string[];
    consumer_license: string[];
    consumer_user: string[];
    reader_license: string[];
    reader_user: string[];
    ttl: number;
}

const isNotUndefinedOrEmpty = <T,>(s: T | undefined): s is T => !!s && s !== '';

interface EditProductCostProps {
    productId: string;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (product: ProductDetail, costs: ProductCost[]) => void;
}

export const EditProductCost: FC<EditProductCostProps> = ({ productId, isOpen, onClose, onSuccess }) => {
    const toast = useToast();
    const selectedGame = useContext(SelectedGameContext);
    const currLicense = selectedGame?.currLicense;
    const [product, setProduct] = useState<ProductDetail>();
    const [costs, setCosts] = useState<ProductCost[]>([]);
    const url = `${config.inventoryServiceURL}`;
    const { keycloak } = useKeycloak();
    const { isOpen: isAlertOpen, onClose: onAlertClose, onOpen: onAlertOpen } = useDisclosure();
    const cancelRef = useRef<HTMLButtonElement>(null);
    const alertFocusRef = useRef<HTMLButtonElement>(null); // The null is needed to appease the type system

    const getProductDef = async (license: string, productId: string) => {
        const dc = new InventoryDataCaller(url, keycloak.token!);
        const productResponse = await dc.getProduct(license, productId);
        if (productResponse.status !== 200) {
            return toast({
                title: `Error`,
                description: `Error getting product definition`,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }

        const products: Array<ProductDetail> = (await productResponse.json()).data;
        if (!products || products.length !== 1) {
            return toast({
                title: `Error`,
                description: `Incorrect number of product definitions returned`,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }

        setProduct(products[0]);
        setCosts(products[0].cost || []);
    };

    const submit = async () => {
        onAlertClose();

        if (!product) {
            console.error("product not defined");
            return;
        }

        const dc = new InventoryDataCaller(url, keycloak.token!);
        const response = await dc.updateProduct({
            ...product,
            cost: costs,
        });

        if (response.status !== 200) {
            return toast({
                title: 'Unexpected Error',
                description: 'There was an error processing your request...soz lol',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } else {
            onSuccess(product, costs);
            onClose();
            return toast({
                title: 'Huzzah!',
                description: 'Updated inventory item quantity successfully!',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleAlertOpen = () => {
        if (costs.some(cost => isNaN(cost.quantity) || cost.quantity <= 0)) {
            return toast({
                title: `Invalid Quantity Error`,
                description: 'Please ensure each cost is greater than 0.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } else {
            onAlertOpen();
        }
    };

    const handleQuantityChange = (newQuantity: string, index: number) => {
        const newQuantityVal = parseFloat(newQuantity);
        if (newQuantityVal <= 0) {
            return;
        }
        setCosts(costs => costs.map((cost, i) => {
            if (i === index) {
                return { ...cost, quantity: newQuantityVal };
            }
            return cost;
        }));
    };

    const handleSubmit = () => {
        submit();
    };

    const handleModalClose = () => {
        onClose();
    };

    useEffect(() => {
        if (productId !== '') {
            getProductDef(currLicense.key, productId);
        }
    }, [currLicense, keycloak.token, url, productId]);

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Edit Cost for "{product?.product_name}"</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Flex direction={'row'} justify={'left'} gap={'20px'}>
                            <Table layout="fixed">
                                <Thead>
                                    <Tr>
                                        <Th>Cost Type</Th>
                                        <Th>Currency</Th>
                                        <Th>Quantity</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {costs.map((cost, i) => (
                                    <Tr key={`cost_${i}`}>
                                        <Td>{cost.cost_type ? 'Virtual Cost' : 'Real Cost'}</Td>
                                        <Td>{cost.cost_type ? cost.cost_type : cost.currency}</Td>
                                        <Td>
                                            <NumberInput
                                                width={'100%'}
                                                defaultValue={cost.quantity}
                                                value={cost.quantity ? cost.quantity : 0}
                                                min={0}
                                                onChange={(newQuantity) => handleQuantityChange(newQuantity, i)}
                                            >
                                                <NumberInputField />
                                                <NumberInputStepper>
                                                <NumberIncrementStepper />
                                                <NumberDecrementStepper />
                                                </NumberInputStepper>
                                            </NumberInput>
                                        </Td>
                                    </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Flex>
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={handleModalClose}>Cancel</Button>
                        <Button onClick={handleAlertOpen} ml={'1em'}>
                            Confirm
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <AlertDialog
                leastDestructiveRef={cancelRef}
                onClose={onAlertClose}
                isOpen={isAlertOpen}
                isCentered
                closeOnEsc
                closeOnOverlayClick
                motionPreset="slideInBottom"
            >
                <AlertDialogOverlay />
                <AlertDialogContent>
                    <AlertDialogHeader>
                        Confirm cost change for {product?.product_name}
                    </AlertDialogHeader>
                    <AlertDialogBody>
                        Confirm that you would like to change the cost of {product?.product_name}{' '}
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={alertFocusRef} onClick={onAlertClose}>
                            Cancel
                        </Button>
                        <Button type="submit" onClick={handleSubmit} ml={'1em'}>
                            Save
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default EditProductCost;
